import type { User } from "@supabase/supabase-js";

import { EMPTY_METADATA } from "./utils";
import type { MeFallbackState } from "./types";

export type DerivedUserProperties = {
  metadata: Record<string, unknown>;
  userId: string | null;
  userEmail: string | null;
  userType: string | null;
  userAccountId: string | null;
  sanitizedMetadataFullName: string | null;
  fullName: string | null;
  pictureUrl: string | null;
  emailVerified: boolean;
  phoneVerified: boolean;
  completed: boolean;
  stepForm: number | null;
  companyId: string | null;
  commercialName: string | null;
  legalName: string | null;
  companyPictureUrl: string | null;
  infoId: string | null;
  profileHealth: number | null;
  urlCv: string | null;
  sanitizedMetadataPhoneNumber: string | null;
  phoneNumber: string | null;
  phoneCountryId: string | null;
};

export type DeriveUserInput = {
  user: User | null;
  impersonatedUserId: string | null;
  impersonatedMetadata: Record<string, unknown> | null;
  meFallback: MeFallbackState;
};

// Impersonation metadata takes precedence; otherwise fall back to user_metadata
// and finally to a frozen empty object so downstream property reads are safe.
export function resolveMetadata(
  user: User | null,
  impersonatedMetadata: Record<string, unknown> | null,
): Record<string, unknown> {
  return impersonatedMetadata ?? user?.user_metadata ?? EMPTY_METADATA;
}

// user_metadata.full_name sometimes arrives as "", " ", "-" or "- -" when the
// signup was incomplete. Normalize those cases to null so downstream code can
// apply the /api/me fallback instead of rendering placeholder junk.
export function sanitizeFullName(metadata: Record<string, unknown>): string | null {
  const raw = typeof metadata.full_name === "string" ? metadata.full_name.trim() : "";
  return raw.replace(/[-\s]/g, "").length > 0 ? raw : null;
}

// Phone can live either at metadata.phone_number or nested at metadata.phone.phone_number.
export function sanitizePhoneNumber(metadata: Record<string, unknown>): string | null {
  const nested = extractNestedPhone(metadata);
  const raw =
    (metadata.phone_number as string | undefined) ??
    (nested?.phone_number as string | undefined) ??
    null;
  return typeof raw === "string" && raw.trim().length > 0 ? raw : null;
}

function extractNestedPhone(
  metadata: Record<string, unknown>,
): Record<string, unknown> | null {
  const phone = metadata.phone;
  return typeof phone === "object" && phone !== null
    ? (phone as Record<string, unknown>)
    : null;
}

export function deriveUserProperties(input: DeriveUserInput): DerivedUserProperties {
  const { user, impersonatedUserId, impersonatedMetadata, meFallback } = input;

  const metadata = resolveMetadata(user, impersonatedMetadata);
  const userId = impersonatedUserId ?? user?.id ?? null;
  const userEmail =
    (impersonatedMetadata?.email as string | undefined) ?? user?.email ?? null;
  const userType = (metadata.user_type as string) ?? null;
  const userAccountId =
    (metadata.user_account_id as string) ?? meFallback.userAccountId ?? null;

  const sanitizedMetadataFullName = sanitizeFullName(metadata);
  const fullName = sanitizedMetadataFullName ?? meFallback.fullName ?? null;

  const pictureUrl =
    (metadata.avatar_url as string) ??
    (metadata.picture as string) ??
    meFallback.pictureUrl ??
    null;
  const emailVerified = (metadata.email_verified as boolean) ?? false;
  const phoneVerified = (metadata.phone_verified as boolean) ?? false;
  const completed = (metadata.completed as boolean) ?? false;
  const stepForm =
    typeof metadata.step_form === "number" ? metadata.step_form : null;

  const companyId = (metadata.company_id as string) ?? null;
  const commercialName = (metadata.commercial_name as string) ?? null;
  const legalName = (metadata.legal_name as string) ?? null;
  const companyPictureUrl = (metadata.company_picture as string) ?? null;

  const infoId = (metadata.info_id as string) ?? null;
  const profileHealth =
    typeof metadata.profile_health === "number" ? metadata.profile_health : null;
  const urlCv = (metadata.url_cv as string) ?? null;

  const nestedPhone = extractNestedPhone(metadata);
  const sanitizedMetadataPhoneNumber = sanitizePhoneNumber(metadata);
  const phoneNumber =
    sanitizedMetadataPhoneNumber ?? meFallback.phoneNumber ?? null;
  const phoneCountryId =
    (metadata.phone_country_id as string) ??
    (nestedPhone?.country_id as string) ??
    null;

  return {
    metadata,
    userId,
    userEmail,
    userType,
    userAccountId,
    sanitizedMetadataFullName,
    fullName,
    pictureUrl,
    emailVerified,
    phoneVerified,
    completed,
    stepForm,
    companyId,
    commercialName,
    legalName,
    companyPictureUrl,
    infoId,
    profileHealth,
    urlCv,
    sanitizedMetadataPhoneNumber,
    phoneNumber,
    phoneCountryId,
  };
}
